import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import AutofillBanner from '../components/AutofillBanner';
import { useUserContext } from '../hooks/useUserContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Target, TrendingUp, AlertCircle, CheckCircle, Loader2, RefreshCw, Sliders, MessageCircle, ArrowRight, Calendar, Sparkles } from 'lucide-react';
import LeadCaptureModal from '../components/LeadCaptureModal';
import AIRefinementPanel from '../components/AIRefinementPanel';
import { useTranslation } from 'react-i18next';
import StepReviewModal from '../components/StepReviewModal';
import VoiceInput from '../components/VoiceInput';
import { useNavigate } from 'react-router-dom';

const BrandAudit = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showRefinementPanel, setShowRefinementPanel] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [formData, setFormData] = useState({
    instagram_url: '',
    bio_message: '',
    target_audience: '',
    visual_universe: '',
    transformation_promise: '',
    current_challenge: ''
  });
  const [dataPreloaded, setDataPreloaded] = useState(false);
  const [hasSeenResults, setHasSeenResults] = useState(false);
  
  // Auto-fill context
  const { 
    hasData: hasContextData, 
    sources, 
    sourcesDisplay, 
    summaryMessage,
    getVision,
    getProfile,
    getAudit,
    loading: contextLoading
  } = useUserContext();

  // Pre-fill form with existing data
  useEffect(() => {
    if (!contextLoading && hasContextData && !dataPreloaded && !result) {
      const vision = getVision();
      const profile = getProfile();
      const existingAudit = getAudit();
      
      let hasChanges = false;
      const updates = {};
      
      // Pre-fill bio message from existing audit or profile
      if (existingAudit?.bio) {
        updates.bio_message = existingAudit.bio;
        hasChanges = true;
      }
      
      // Pre-fill target audience from profile or vision
      const positioning = profile?.unique_positioning || vision?.unique_value_proposition;
      if (positioning) {
        updates.target_audience = positioning;
        hasChanges = true;
      }
      
      // Pre-fill transformation from vision
      if (vision?.transformation_promise) {
        updates.transformation_promise = vision.transformation_promise;
        hasChanges = true;
      }
      
      // Pre-fill visual universe from vision
      if (vision?.visual_direction) {
        let visualText = '';
        const visualDir = vision.visual_direction;
        
        if (typeof visualDir === 'object' && visualDir !== null) {
          if (visualDir.primary_aesthetic) {
            visualText = visualDir.primary_aesthetic;
          }
          if (visualDir.color_energy) {
            visualText += visualText ? '\n' + visualDir.color_energy : visualDir.color_energy;
          }
          if (!visualText) {
            const firstValue = Object.values(visualDir).find(v => typeof v === 'string' && v.length > 10);
            visualText = firstValue || '';
          }
        } else if (typeof visualDir === 'string') {
          visualText = visualDir;
        }
        
        if (visualText) {
          updates.visual_universe = visualText.substring(0, 500);
          hasChanges = true;
        }
      }
      
      if (hasChanges) {
        setFormData(prev => ({
          ...prev,
          ...updates
        }));
        setDataPreloaded(true);
      }
    }
  }, [contextLoading, hasContextData, dataPreloaded, result, getVision, getProfile, getAudit]);

  useEffect(() => {
    fetchHistory();
    // Check if we should show lead capture modal for free users
    checkLeadCapture();
  }, []);

  const checkLeadCapture = () => {
    const tier = user?.subscription_tier || 'free';
    const leadCaptured = localStorage.getItem('lead_captured');
    
    // Show modal after 3 seconds for free users who haven't been captured
    if (tier === 'free' && !leadCaptured) {
      setTimeout(() => {
        setShowLeadModal(true);
      }, 3000);
    }
  };

  const fetchHistory = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/brand-audit/history`, { headers });
      setHistory(response.data.audits);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/brand-audit/analyze`, formData, { 
        headers,
        timeout: 120000 // 120 second timeout for AI analysis (mobile networks can be slow)
      });
      setResult(response.data);
      setHasSeenResults(false); // Reset - user hasn't seen results yet
      toast.success('Audit de marque terminé !');
      fetchHistory();
      // Review will be shown after user clicks "J'ai compris" button
    } catch (error) {
      console.error('Brand audit error:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('L\'analyse prend plus de temps que prévu. Réessaie dans quelques secondes.');
      } else if (error.response?.status === 500) {
        toast.error('Erreur serveur. Réessaie dans quelques instants.');
      } else if (!navigator.onLine) {
        toast.error('Vérifie ta connexion internet et réessaie.');
      } else {
        toast.error(error.response?.data?.detail || 'Une erreur est survenue. Réessaie.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-[#E5C585]';
    return 'text-red-400';
  };

  return (
    <DashboardLayout title="Audit de Marque">
      <div className="max-w-6xl mx-auto">
        {/* Auto-fill Banner */}
        {hasContextData && dataPreloaded && !result && (
          <AutofillBanner
            hasData={hasContextData}
            sources={sources}
            sourcesDisplay={sourcesDisplay}
            message={summaryMessage}
            showDetails={true}
          />
        )}
        
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="card-premium p-8">
              <div className="mb-6">
                <Target className="text-[#E5C585] mb-4" size={28} />
                <h2 className="font-serif text-2xl text-white mb-2">{t('brandAudit.analyzeTitle')}</h2>
                <p className="text-[#A1A1AA]">{t('brandAudit.analyzeSubtitle')}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* 1. Instagram URL (optional) */}
                <div>
                  <Label className="text-[#A1A1AA] text-sm mb-2 block">
                    Lien de ton Instagram <span className="text-[#52525B]">(optionnel mais recommandé)</span>
                  </Label>
                  <input
                    type="url"
                    value={formData.instagram_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, instagram_url: e.target.value }))}
                    placeholder="https://instagram.com/ton_compte"
                    className="w-full bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-4 text-white placeholder:text-[#52525B] focus:outline-none"
                    data-testid="audit-instagram-input"
                  />
                  <p className="text-[#52525B] text-xs mt-1">Permet d'analyser ta bio, ton contenu et ta cohérence visuelle</p>
                </div>

                {/* 2. Bio Message */}
                <div>
                  <Label className="text-[#A1A1AA] text-sm mb-2 block">
                    Quel message principal veux-tu faire passer en 1 phrase dans ta bio ? *
                  </Label>
                  <div className="relative">
                    <Textarea
                      value={formData.bio_message}
                      onChange={(e) => setFormData(prev => ({ ...prev, bio_message: e.target.value }))}
                      placeholder="Ex: J'aide les femmes entrepreneures à créer une marque qui leur ressemble..."
                      className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-4 pr-14 min-h-[80px] text-white placeholder:text-[#52525B]"
                      required
                      data-testid="audit-bio-input"
                    />
                    <div className="absolute right-3 bottom-3">
                      <VoiceInput 
                        onTranscription={(text) => {
                          setFormData(prev => ({ 
                            ...prev, 
                            bio_message: prev.bio_message ? `${prev.bio_message} ${text}` : text 
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* 3. Target Audience */}
                <div>
                  <Label className="text-[#A1A1AA] text-sm mb-2 block">
                    Aujourd'hui, tu aides qui et à faire quoi concrètement ? *
                  </Label>
                  <div className="relative">
                    <Textarea
                      value={formData.target_audience}
                      onChange={(e) => setFormData(prev => ({ ...prev, target_audience: e.target.value }))}
                      placeholder="Ex: J'accompagne les coachs et thérapeutes à structurer leur offre et attirer leurs clients idéaux..."
                      className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-4 pr-14 min-h-[80px] text-white placeholder:text-[#52525B]"
                      required
                      data-testid="audit-positioning-input"
                    />
                    <div className="absolute right-3 bottom-3">
                      <VoiceInput 
                        onTranscription={(text) => {
                          setFormData(prev => ({ 
                            ...prev, 
                            target_audience: prev.target_audience ? `${prev.target_audience} ${text}` : text 
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* 4. Visual Universe */}
                <div>
                  <Label className="text-[#A1A1AA] text-sm mb-2 block">
                    Comment décrirais-tu l'univers visuel de ta marque ? *
                  </Label>
                  <div className="relative">
                    <Textarea
                      value={formData.visual_universe}
                      onChange={(e) => setFormData(prev => ({ ...prev, visual_universe: e.target.value }))}
                      placeholder="Couleurs, ambiance, style... Ex: Tons chauds et dorés, photos lumineuses, style épuré et élégant..."
                      className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-4 pr-14 min-h-[80px] text-white placeholder:text-[#52525B]"
                      required
                      data-testid="audit-visual-input"
                    />
                    <div className="absolute right-3 bottom-3">
                      <VoiceInput 
                        onTranscription={(text) => {
                          setFormData(prev => ({ 
                            ...prev, 
                            visual_universe: prev.visual_universe ? `${prev.visual_universe} ${text}` : text 
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* 5. Transformation Promise */}
                <div>
                  <Label className="text-[#A1A1AA] text-sm mb-2 block">
                    Quelle transformation promets-tu à tes clientes ? *
                  </Label>
                  <div className="relative">
                    <Textarea
                      value={formData.transformation_promise}
                      onChange={(e) => setFormData(prev => ({ ...prev, transformation_promise: e.target.value }))}
                      placeholder="Ex: Passer d'une marque floue à une identité claire et magnétique qui attire naturellement..."
                      className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-4 pr-14 min-h-[80px] text-white placeholder:text-[#52525B]"
                      required
                      data-testid="audit-transformation-input"
                    />
                    <div className="absolute right-3 bottom-3">
                      <VoiceInput 
                        onTranscription={(text) => {
                          setFormData(prev => ({ 
                            ...prev, 
                            transformation_promise: prev.transformation_promise ? `${prev.transformation_promise} ${text}` : text 
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* 6. Current Challenge (optional) */}
                <div>
                  <Label className="text-[#A1A1AA] text-sm mb-2 block">
                    Qu'est-ce qui te semble flou ou difficile dans ta marque aujourd'hui ? <span className="text-[#52525B]">(optionnel)</span>
                  </Label>
                  <div className="relative">
                    <Textarea
                      value={formData.current_challenge}
                      onChange={(e) => setFormData(prev => ({ ...prev, current_challenge: e.target.value }))}
                      placeholder="Ex: Je n'arrive pas à me différencier, ma communication manque de cohérence..."
                      className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-4 pr-14 min-h-[70px] text-white placeholder:text-[#52525B]"
                      data-testid="audit-challenge-input"
                    />
                    <div className="absolute right-3 bottom-3">
                      <VoiceInput 
                        onTranscription={(text) => {
                          setFormData(prev => ({ 
                            ...prev, 
                            current_challenge: prev.current_challenge ? `${prev.current_challenge} ${text}` : text 
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !formData.bio_message.trim() || !formData.target_audience.trim() || !formData.visual_universe.trim() || !formData.transformation_promise.trim()}
                  className="w-full btn-gold py-6 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="audit-submit-btn"
                  style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                  onClick={(e) => {
                    // iOS Safari fix: Ensure click event fires
                    if (!loading && formData.bio_message.trim() && formData.target_audience.trim() && formData.visual_universe.trim() && formData.transformation_promise.trim()) {
                      handleSubmit(e);
                    }
                  }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      {t('brandAudit.analyzing')}
                    </>
                  ) : (
                    t('brandAudit.analyzeButton')
                  )}
                </Button>
              </form>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {result ? (
              <div className="space-y-6">
                {/* Score Card with Diagnostic */}
                <div className="card-premium p-8 text-center">
                  <span className="font-accent text-[#E5C585] text-xs tracking-widest">{t('brandAudit.brandScore')}</span>
                  <div className={`font-serif text-7xl mt-4 ${getScoreColor(result.brand_score)}`}>
                    {result.brand_score}
                  </div>
                  <p className="text-[#A1A1AA] mt-2">{t('brandAudit.outOf100')}</p>
                  {result.diagnostic_summary && (
                    <p className="text-[#E5C585] mt-4 italic text-sm border-t border-[#27272A] pt-4">
                      "{result.diagnostic_summary}"
                    </p>
                  )}
                </div>

                {/* WOW Insight - New Premium Section */}
                {result.wow_insight && (
                  <div className="card-gold p-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-[#D4AF37]/20 to-transparent" />
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">💡</span>
                      <div>
                        <h3 className="font-serif text-lg text-white mb-2">Insight clé</h3>
                        <p className="text-[#E5C585] leading-relaxed">{result.wow_insight}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Interactive AI Buttons */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleSubmit();
                    }}
                    className="flex-1 border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37] py-3"
                    data-testid="regenerate-btn"
                  >
                    <RefreshCw size={16} className="mr-2" />
                    {t('brandAudit.regenerate')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowRefinementPanel(true)}
                    className="flex-1 border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37] py-3"
                    data-testid="adjust-btn"
                  >
                    <Sliders size={16} className="mr-2" />
                    {t('brandAudit.adjust')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowRefinementPanel(true)}
                    className="flex-1 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 py-3"
                    data-testid="ask-ai-btn"
                  >
                    <MessageCircle size={16} className="mr-2" />
                    {t('brandAudit.askAi')}
                  </Button>
                </div>

                {/* Strengths */}
                <div className="card-premium p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle className="text-green-400" size={20} />
                    <h3 className="font-serif text-lg text-white">{t('brandAudit.strengths')}</h3>
                  </div>
                  <ul className="space-y-3">
                    {result.strengths?.map((item, i) => (
                      <li key={i} className="text-[#A1A1AA] flex items-start gap-3">
                        <span className="text-green-400 mt-1">✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Priority Actions - New Enhanced Section */}
                {result.priority_actions && result.priority_actions.length > 0 && (
                  <div className="card-premium p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <Target className="text-[#D4AF37]" size={20} />
                      <h3 className="font-serif text-lg text-white">Plan d'action par priorité</h3>
                    </div>
                    <div className="space-y-6">
                      {result.priority_actions.map((action, i) => (
                        <div key={i} className="border-l-2 border-[#D4AF37] pl-4 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-accent tracking-wider px-2 py-1 ${
                              action.priority === 1 ? 'bg-[#D4AF37] text-black' :
                              action.priority === 2 ? 'bg-[#D4AF37]/30 text-[#D4AF37]' :
                              'bg-[#27272A] text-[#A1A1AA]'
                            }`}>
                              PRIORITÉ {action.priority}
                            </span>
                            <h4 className="text-white font-medium">{action.title}</h4>
                          </div>
                          <p className="text-[#71717A] text-sm">
                            <span className="text-[#A1A1AA]">Problème :</span> {action.problem}
                          </p>
                          <p className="text-[#A1A1AA]">
                            <span className="text-[#E5C585]">→</span> {action.solution}
                          </p>
                          {action.example && (
                            <div className="bg-[#0A0A0A] border border-[#1C1C1C] p-3 mt-2">
                              <p className="text-xs text-[#52525B] mb-1">Exemple concret :</p>
                              <p className="text-[#E5C585] text-sm italic">"{action.example}"</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bio Rewrite - New Section */}
                {result.bio_rewrite && (
                  <div className="card-premium p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-xl">✍️</span>
                      <h3 className="font-serif text-lg text-white">Ta nouvelle bio Instagram</h3>
                    </div>
                    {result.bio_rewrite.before_analysis && (
                      <p className="text-[#71717A] text-sm mb-4">{result.bio_rewrite.before_analysis}</p>
                    )}
                    <div className="bg-gradient-to-br from-[#D4AF37]/10 to-[#0A0A0A] border border-[#D4AF37]/30 p-4 relative">
                      <p className="text-white leading-relaxed whitespace-pre-line">{result.bio_rewrite.suggested_bio}</p>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(result.bio_rewrite.suggested_bio);
                          toast.success('Bio copiée !');
                        }}
                        className="absolute top-2 right-2 text-[#D4AF37] hover:text-[#E5C585] text-xs border border-[#D4AF37]/30 px-2 py-1 hover:bg-[#D4AF37]/10"
                      >
                        Copier
                      </button>
                    </div>
                    {result.bio_rewrite.why_it_works && (
                      <p className="text-[#A1A1AA] text-sm mt-3 flex items-start gap-2">
                        <span className="text-[#D4AF37]">💡</span>
                        {result.bio_rewrite.why_it_works}
                      </p>
                    )}
                  </div>
                )}

                {/* 30-Day Plan - Simplified Projection */}
                {result.transformation_plan && (
                  <div className="card-gold p-6 space-y-6 text-center">
                    {/* Header */}
                    <div>
                      <span className="text-4xl mb-3 block">🚀</span>
                      <h3 className="font-serif text-2xl text-white mb-2">
                        {result.transformation_plan.headline || "Ton plan Vision Lab – 30 jours"}
                      </h3>
                      <p className="text-[#E5C585] leading-relaxed">
                        {result.transformation_plan.promise}
                      </p>
                    </div>
                    
                    {/* What you'll achieve */}
                    <div className="text-left space-y-3 pt-4 border-t border-[#D4AF37]/20">
                      <p className="text-[#E5C585] text-sm">En 30 jours, tu vas :</p>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-3 text-[#A1A1AA]">
                          <span className="text-[#D4AF37] mt-0.5">✦</span>
                          <span><strong className="text-white">Clarifier ton message</strong> pour que les bonnes personnes te comprennent instantanément</span>
                        </li>
                        <li className="flex items-start gap-3 text-[#A1A1AA]">
                          <span className="text-[#D4AF37] mt-0.5">✦</span>
                          <span><strong className="text-white">Structurer ta marque</strong> pour arrêter de poster sans direction</span>
                        </li>
                        <li className="flex items-start gap-3 text-[#A1A1AA]">
                          <span className="text-[#D4AF37] mt-0.5">✦</span>
                          <span><strong className="text-white">Créer une présence magnétique</strong> qui attire naturellement</span>
                        </li>
                      </ul>
                    </div>
                    
                    {/* Coach message */}
                    <div className="text-[#A1A1AA] text-sm py-2">
                      <p>Je vais te guider chaque jour.</p>
                      <p className="text-[#D4AF37]">Tu n'as rien à deviner.</p>
                    </div>
                    
                    {/* CTA */}
                    <Button
                      onClick={() => navigate('/vision-plan')}
                      className="btn-gold py-4 px-8 text-base w-full flex items-center justify-center gap-2"
                    >
                      COMMENCER MON PLAN
                      <ArrowRight size={18} />
                    </Button>
                    
                    {/* 90-day teaser */}
                    {result.transformation_plan.ninety_day_teaser && (
                      <p className="text-[#71717A] text-xs italic pt-2 border-t border-[#D4AF37]/10">
                        ✨ {result.transformation_plan.ninety_day_teaser}
                      </p>
                    )}
                  </div>
                )}

                {/* Legacy sections for backward compatibility */}
                {!result.priority_actions && result.improvements && (
                  <div className="card-premium p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <AlertCircle className="text-[#E5C585]" size={20} />
                      <h3 className="font-serif text-lg text-white">{t('brandAudit.improvements')}</h3>
                    </div>
                    <ul className="space-y-3">
                      {result.improvements?.map((item, i) => (
                        <li key={i} className="text-[#A1A1AA] flex items-start gap-3">
                          <span className="text-[#D4AF37] mt-1">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {!result.priority_actions && result.strategic_recommendations && (
                  <div className="card-premium p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <TrendingUp className="text-[#D4AF37]" size={20} />
                      <h3 className="font-serif text-lg text-white">{t('brandAudit.recommendations')}</h3>
                    </div>
                    <ul className="space-y-3">
                      {result.strategic_recommendations?.map((item, i) => (
                        <li key={i} className="text-[#A1A1AA] flex items-start gap-3">
                          <span className="text-[#D4AF37] mt-1">{i + 1}.</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Next Action CTA */}
                {result.next_step?.action && (
                  <div className="card-premium p-6 text-center border-[#D4AF37]/30">
                    <p className="text-[#52525B] text-xs uppercase tracking-wider mb-2">Prochaine action</p>
                    <p className="text-white font-medium">{result.next_step.action}</p>
                  </div>
                )}

                {/* Call to Action - Triggers Review */}
                {!hasSeenResults && (
                  <div className="card-gold p-6 text-center">
                    <p className="text-white mb-4">As-tu pris le temps de lire tes résultats ?</p>
                    <Button
                      onClick={() => {
                        setHasSeenResults(true);
                        setShowReviewModal(true);
                      }}
                      className="btn-gold px-8 py-3"
                      data-testid="understood-btn"
                    >
                      J'ai compris, donner mon avis ✨
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="card-premium p-12 text-center h-full flex flex-col items-center justify-center">
                <Target className="text-[#27272A] mb-6" size={64} />
                <h3 className="font-serif text-xl text-white mb-3">{t('brandAudit.noAuditYet')}</h3>
                <p className="text-[#A1A1AA] max-w-sm">
                  {t('brandAudit.noAuditDesc')}
                </p>
              </div>
            )}
          </motion.div>
        </div>

        {/* History */}
        {history.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-12"
          >
            <h2 className="font-serif text-2xl text-white mb-6">{t('brandAudit.previousAudits')}</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {history.slice(0, 6).map((audit) => (
                <button
                  key={audit.audit_id}
                  onClick={() => setResult(audit)}
                  className="card-premium p-4 text-left hover:border-[#D4AF37]/50 transition-colors"
                  data-testid={`audit-history-${audit.audit_id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-serif text-2xl ${getScoreColor(audit.brand_score)}`}>
                      {audit.brand_score}
                    </span>
                    <span className="text-[#52525B] text-xs">
                      {new Date(audit.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-[#A1A1AA] text-sm truncate">
                    {audit.input_data?.positioning?.slice(0, 50)}...
                  </p>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </div>
      
      {/* Lead Capture Modal */}
      <LeadCaptureModal 
        isOpen={showLeadModal} 
        onClose={() => setShowLeadModal(false)}
        source="brand_audit"
      />
      
      {/* AI Refinement Panel */}
      <AIRefinementPanel
        isOpen={showRefinementPanel}
        onClose={() => setShowRefinementPanel(false)}
        toolType="brand_audit"
        currentResult={result}
        onResultUpdate={(newResult) => {
          setResult({ ...result, ...newResult });
          toast.success('Results updated with your refinements!');
        }}
        suggestedPrompts={[
          "Make recommendations more specific to my niche",
          "Focus more on visual branding",
          "Give me quick wins I can implement today",
          "How can I improve my Instagram bio?"
        ]}
      />

      {/* Step Review Modal */}
      <StepReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        stepId="brand_audit"
        stepName="Analyser ta marque"
        onValidated={() => setShowReviewModal(false)}
      />
    </DashboardLayout>
  );
};

export default BrandAudit;
